import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Form, FormTextarea, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { CountrySelectionDropDown } from "../../common/components/CountrySelectionDropDown";
import { isUSA } from "../../common/utils";
import { SharedAddressInput } from "../SharedAddressInput";
import { BicLookupFields } from "./Components/BicLookupFields";
import { CurrencySelect } from "./Components/CurrencySelect";
import { FormCountrySelect } from "./Components/FormCountrySelect";
import { LocalRoutingFields } from "./Components/LocalRoutingFields";
import { useCreateBankAccount } from "./hooks/useCreateBankAccount";
import { useEntityCountryCurrency } from "./hooks/useEntityCountryCurrency";
import { useWorkflowAdoption } from "./hooks/useWorkflowAdoption";
import {
  accountFieldLabel,
  BankAndAccountStepInputFields,
  type BankAccountFormValues,
  buildBankAccountFormSchema,
  CountryCurrencyStepInputFields,
  localRoutingFieldsForCountry,
  LocalRoutingInputFields,
  OwnerStepFields,
  type OwnerStepValues,
} from "./schema";
import { errorBlockStyles, formGridStyles } from "./styles_vanilla.css";
import {
  type BankAccountOnboardingData,
  type BankAccountOnboardingWorkflowProps,
} from "./types";

// Verified against BE-5 schema once the mutation ships; update if the server
// uses a different code or field name for this condition.
const CURRENCY_MISMATCH_MESSAGE = "CURRENCY_MISMATCH";

const emptyOwnerAddress: OwnerStepValues[OwnerStepFields.OwnerAddress] = {
  country: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  state: "",
  province: "",
  zip: "",
};

const blankFormValues: BankAccountFormValues = {
  [CountryCurrencyStepInputFields.Country]: "",
  [CountryCurrencyStepInputFields.Currency]: "",
  [BankAndAccountStepInputFields.SwiftBic]: "",
  [BankAndAccountStepInputFields.BankNameIntl]: "",
  [BankAndAccountStepInputFields.AccountNumberOrIban]: "",
  [LocalRoutingInputFields.SortCode]: "",
  [LocalRoutingInputFields.Bsb]: "",
  [LocalRoutingInputFields.TransitNumber]: "",
  [LocalRoutingInputFields.InstitutionNumber]: "",
  [OwnerStepFields.OwnerName]: "",
  [OwnerStepFields.OwnerAddress]: emptyOwnerAddress,
  [OwnerStepFields.Notes]: "",
};

/**
 * Bank account onboarding as a single-page modal form. Country, currency, bank
 * and account details, and owner details are all collected on one page; the
 * local routing fields and the account-number label follow the selected bank
 * country. In US-centric mode the country and currency selectors are hidden and
 * the account is always treated as a plain account number (never an IBAN); the
 * `useCreateBankAccount` hook strips country/currency/localRouting from the
 * mutation input in that mode.
 */
export const BankAccountOnboardingWorkflow: React.FC<BankAccountOnboardingWorkflowProps> = ({
  show,
  entityId,
  arenaId,
  onExit,
  onSuccess,
  usCentricMode,
  initialBankAccount,
}) => {
  const { track } = useWorkflowAdoption();

  const { countryAlpha2, loading } = useEntityCountryCurrency(entityId);

  const {
    createBankAccount,
    loading: submitting,
    mutationErrors,
  } = useCreateBankAccount({
    usCentricMode,
    onSuccess,
    onSettled: (succeeded) => track(succeeded ? "succeeded" : "failed"),
  });

  // Freeze the initial values once any entity pre-fill has resolved, so later
  // user edits are never reset by a re-render. Until then the form renders with
  // blank values; the `key` flip when `loading` clears reseeds it exactly once.
  const prefilled = useRef<BankAccountFormValues>();
  if (!prefilled.current && !loading) {
    prefilled.current = {
      ...blankFormValues,
      [CountryCurrencyStepInputFields.Country]:
        initialBankAccount?.country ?? countryAlpha2 ?? "",
      // Currency is never pre-filled from the entity; it starts blank in create
      // mode and only carries a value that an existing account already had.
      [CountryCurrencyStepInputFields.Currency]: initialBankAccount?.currency ?? "",
      [BankAndAccountStepInputFields.SwiftBic]: initialBankAccount?.swiftBic ?? "",
      [BankAndAccountStepInputFields.BankNameIntl]: initialBankAccount?.bankNameIntl ?? "",
      [LocalRoutingInputFields.SortCode]: initialBankAccount?.localRouting?.sortCode ?? "",
      [LocalRoutingInputFields.Bsb]: initialBankAccount?.localRouting?.bsb ?? "",
      [LocalRoutingInputFields.TransitNumber]:
        initialBankAccount?.localRouting?.transitNumber ?? "",
      [LocalRoutingInputFields.InstitutionNumber]:
        initialBankAccount?.localRouting?.institutionNumber ?? "",
      [OwnerStepFields.OwnerName]: initialBankAccount?.ownerName ?? "",
      // The owner address defaults to the bank country for non-US non-US-centric
      // flows, where the address is required. Skipping it for US (and US-centric
      // mode) avoids "starting" the optional-address schema: the lazy validator
      // treats any non-blank field as the address having been started, which
      // would then require street, city, state, and ZIP.
      [OwnerStepFields.OwnerAddress]: initialBankAccount?.ownerAddress ?? {
        ...emptyOwnerAddress,
        country:
          usCentricMode || isUSA(initialBankAccount?.country ?? countryAlpha2)
            ? ""
            : initialBankAccount?.country ?? countryAlpha2 ?? "",
      },
      [OwnerStepFields.Notes]: initialBankAccount?.notes ?? "",
    };
  }
  const initialValues = prefilled.current ?? blankFormValues;

  // The country that drives the schema and the rendered routing fields. Held in
  // React state (not read from Formik) so the schema — a prop computed above the
  // Formik provider — can react to a country change on the same form.
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    initialBankAccount?.country,
  );
  useEffect(() => {
    if (selectedCountry === undefined && prefilled.current) {
      const prefilledCountry = prefilled.current[CountryCurrencyStepInputFields.Country];
      if (prefilledCountry) {
        setSelectedCountry(prefilledCountry);
      }
    }
    // `loading` gates when the pre-fill ref becomes populated.
  }, [selectedCountry, loading]);

  // The component stays mounted while `show` toggles, so ModalV2 can play its
  // exit animation. Because it is not unmounted between opens, the form is reset
  // explicitly on each re-open: bumping `formInstance` remounts the Formik form
  // with its initial values, and `selectedCountry` is returned to its seed. The
  // first open is already fresh from mount, so only re-opens reset. "opened" is
  // tracked on the same rising edge, replacing the former mount-time call.
  const [formInstance, setFormInstance] = useState(0);
  const isShown = useRef(false);
  const hasOpened = useRef(false);
  useEffect(() => {
    if (show && !isShown.current) {
      isShown.current = true;
      track("opened");
      if (hasOpened.current) {
        setFormInstance((instance) => instance + 1);
        setSelectedCountry(initialBankAccount?.country);
      }
      hasOpened.current = true;
    } else if (!show) {
      isShown.current = false;
    }
  }, [show, track, initialBankAccount]);

  const validationSchema = useMemo(
    () => buildBankAccountFormSchema({ country: selectedCountry, usCentricMode }),
    [selectedCountry, usCentricMode],
  );

  const handleSubmit = useCallback(
    async (values: BankAccountFormValues) => {
      track("submitted");
      const data: BankAccountOnboardingData = {
        country: values[CountryCurrencyStepInputFields.Country] || undefined,
        currency: values[CountryCurrencyStepInputFields.Currency] || undefined,
        swiftBic: values[BankAndAccountStepInputFields.SwiftBic] || undefined,
        bankNameIntl: values[BankAndAccountStepInputFields.BankNameIntl] || undefined,
        accountNumberOrIban:
          values[BankAndAccountStepInputFields.AccountNumberOrIban] || undefined,
        // Only the selected country's identifiers are collected; the hook drops
        // localRouting entirely in US-centric mode.
        localRouting: Object.fromEntries(
          localRoutingFieldsForCountry(
            values[CountryCurrencyStepInputFields.Country],
            usCentricMode,
          ).map((field) => [field, values[field]]),
        ),
        ownerName: values[OwnerStepFields.OwnerName] || undefined,
        ownerAddress: values[OwnerStepFields.OwnerAddress],
        notes: values[OwnerStepFields.Notes] || undefined,
        usAccount: usCentricMode,
      };
      await createBankAccount(data, arenaId);
    },
    [track, createBankAccount, arenaId, usCentricMode],
  );

  const hasCurrencyMismatch = mutationErrors.some((err) =>
    err.message.includes(CURRENCY_MISMATCH_MESSAGE),
  );

  return (
    <ModalV2
      show={show}
      onHide={onExit}
      title="Create a new bank account"
      isCentered
      size="large"
      data-testid="bank-account-onboarding-modal"
    >
      <Form<BankAccountFormValues>
        key={`${formInstance}-${loading ? "prefill-pending" : "ready"}`}
        initialValues={initialValues}
        yupValidationSchema={validationSchema}
        submitOnlyChangedValues={false}
        onSubmit={handleSubmit}
      >
        <ModalV2.Body>
          <div className={formGridStyles}>
            <Flex flexDirection="column" gap="md">
              {!usCentricMode && (
                <FormCountrySelect disabled={loading} onCountryChange={setSelectedCountry} />
              )}
              <FormTextInput
                isFullWidth
                name={BankAndAccountStepInputFields.AccountNumberOrIban}
                label={accountFieldLabel(selectedCountry)}
              />
              <BicLookupFields />
              <LocalRoutingFields country={selectedCountry} usAccount={usCentricMode} />
            </Flex>

            <Flex flexDirection="column" gap="md">
              {!usCentricMode && (
                <CurrencySelect
                  name={CountryCurrencyStepInputFields.Currency}
                  label="Currency"
                  disabled={loading}
                />
              )}
              <Flex flexDirection="column" gap={1}>
                <FormTextInput
                  isFullWidth
                  name={OwnerStepFields.OwnerName}
                  label="Account owner name"
                />
                <Text variant="secondary-sm">60 characters max</Text>
              </Flex>
              <SharedAddressInput
                title="Account owner address"
                meta={{ headerType: "strong-md" }}
                name={OwnerStepFields.OwnerAddress}
                CountrySelect={CountrySelectionDropDown}
                countryPosition="last"
                showStreetLine2={false}
              />
              <Flex flexDirection="column" gap={2}>
                <Flex gap={2}>
                  <Text variant="strong-md">Notes</Text>
                  <Text variant="secondary-md">(optional)</Text>
                </Flex>
                <FormTextarea
                  isFullWidth
                  size="small"
                  name={OwnerStepFields.Notes}
                  data-testid={`${OwnerStepFields.Notes}-textarea`}
                />
              </Flex>
            </Flex>
          </div>

          {mutationErrors.length > 0 && (
            <Flex
              flexDirection="column"
              gap={2}
              data-testid="bank-account-form-errors"
              className={errorBlockStyles}
            >
              {hasCurrencyMismatch && (
                <Text variant="regular-md" color="error">
                  The account currency does not match the entity currency. To fix this, update
                  the entity currency in entity settings, then resubmit.
                </Text>
              )}
              {mutationErrors
                .filter((err) => !err.message.includes(CURRENCY_MISMATCH_MESSAGE))
                .map((err, i) => (
                  <Text key={i} variant="regular-md" color="error">
                    {err.message}
                  </Text>
                ))}
            </Flex>
          )}
        </ModalV2.Body>

        <ModalV2.Footer>
          <Flex gap="sm" justifyContent="flex-end">
            <Button variant="neutral" onClick={onExit} disabled={submitting}>
              Cancel
            </Button>
            <SubmitButton>Create account</SubmitButton>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};

export default BankAccountOnboardingWorkflow;
